// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { observable, computed, action, makeObservable } from 'mobx';

import { Cluster, ClusterDTO, ClusterEngine, ClusterEngineDTO } from '@shared/models/clusters';
import BaseStore from '@core/stores/base';
import container from '@core/di';
import ClustersService from '@shared/services/clusters';
import { ListRequestParams, PageInfo } from '@shared/types/services';
import { getQueries } from '@shared/utils/common';

@injectable()
export default class ClustersStore extends BaseStore<ClusterDTO, Cluster, ClustersService> {
  static diToken = Symbol('clusters-store');
  @observable _clusterEngines: Array<ClusterEngine> = [];
  @observable _pageInfoEngines: PageInfo;

  constructor() {
    super({ service: container.get<ClustersService>(ClustersService.diToken) });

    this.loading = {
      clusterEngines: false,
      list: false
    };
    makeObservable(this);
  }

  @computed get clusterEngines() {
    return this._clusterEngines;
  }

  @computed get pageInfoEngines() {
    return this._pageInfoEngines;
  }

  getClusterEngine = (name: ClusterEngineDTO['engineName']) => { // TODO replace name for id when backend will be ready
    return this.clusterEngines.find((clusterEngine) => clusterEngine.engineName == name);
  }

  @action getClusterEngines = async (customerName: ClusterDTO['clusterName']) => {
    this.loading.clusterEngines = true;

    const queries: ListRequestParams = getQueries() as unknown as ListRequestParams;

    try {
      const { items, pageInfo } = await this.service.getClusterEngines(queries, customerName);

      this._clusterEngines = items;
      this._pageInfoEngines = pageInfo;

      this.loading.clusterEngines = false;
    } catch {
      this.loading.clusterEngines = false;
    }
  }
}